import { Trigger, TriggerLog } from '@/components/triggers/triggerTypes';
import { StateCreator } from 'zustand';
import { CedarStore } from './types';
import { Json } from '@/database.types';

import {
	createTriggerLog,
	updateTriggerLog,
} from '@/components/triggers/triggerSupabase';
import { checkTrigger } from '@/components/triggers/TriggerUtils';
import { v4 as uuidv4 } from 'uuid';
import { ActionInput, SurveyAction } from './actionsSlice';

// Define loading state type
export type LoadingState = null | 'loading' | 'success' | 'failed';

// Interface for Trigger state
export interface TriggerSlice {
	triggers: Trigger[];
	triggerLogs: TriggerLog[];
	triggerLoadingState: LoadingState;
	setTriggerLoadingState: (state: LoadingState) => void;
	setTriggers: (triggers: Trigger[]) => void;
	setTriggerLogs: (logs: TriggerLog[]) => void;
	callTrigger: (
		triggerId: string,
		callback?: (markComplete: () => Promise<boolean>) => void
	) => Promise<boolean>;
	markTriggerLogComplete: (logId: string) => Promise<boolean>;
	createTriggerLog: (
		triggerId: string,
		completed?: boolean,
		metadata?: Json
	) => Promise<TriggerLog | null>;
	// Add other actions if needed
}

// Create the slice using StateCreator
export const createTriggerSlice: StateCreator<
	CedarStore,
	[],
	[],
	TriggerSlice
> = (set, get) => ({
	triggers: [],
	triggerLogs: [],
	triggerLoadingState: null,

	// Actions
	setTriggerLoadingState: (state: LoadingState) =>
		set({ triggerLoadingState: state }),
	setTriggers: (triggers: Trigger[]) => set({ triggers }),
	setTriggerLogs: (logs: TriggerLog[]) => set({ triggerLogs: logs }),

	/**
	 * Creates a trigger log for the specified trigger ID
	 * @param triggerId ID of the trigger to create a log for
	 * @param completed Whether the log should be marked as completed (default: false)
	 * @param metadata Optional metadata to store with the log
	 * @returns Promise<TriggerLog | null> The created log or null if there was an error
	 */
	createTriggerLog: async (
		triggerId: string,
		completed = false,
		metadata: Json = null
	): Promise<TriggerLog | null> => {
		const { userId } = get();

		// Ensure userId is available
		if (!userId) {
			console.error('User ID is required to create a trigger log');
			return null;
		}

		const { log, error } = await createTriggerLog(triggerId, userId, {
			completed,
			metadata,
		});

		if (error) {
			console.error('Failed to create trigger log:', error);
			return null;
		}

		if (log) {
			// Add the new log to the local state
			const newLog = log as TriggerLog;
			set((state) => ({
				triggerLogs: [...state.triggerLogs, newLog],
			}));
			return newLog;
		}

		return null;
	},

	/**
	 * Marks a trigger log as completed
	 * @param logId ID of the trigger log to mark as completed
	 * @returns Promise<boolean> indicating success or failure
	 */
	markTriggerLogComplete: async (logId: string): Promise<boolean> => {
		const { log: updatedLog, error } = await updateTriggerLog(logId, {
			completed: true,
		});

		if (error) {
			console.error('Failed to update trigger log as completed:', error);
			return false;
		}

		if (updatedLog) {
			// Update the local store with the updated log
			set((state) => ({
				triggerLogs: state.triggerLogs.map((log) =>
					log.id === logId ? updatedLog : log
				),
			}));
			return true;
		}

		return false;
	},

	/**
	 * Calls a specific trigger by ID, checks conditions, and invokes actions if satisfied
	 * @param triggerId ID of the trigger to call
	 * @param callback Optional callback function to execute for 'callback' type actions
	 * @returns Promise<boolean> indicating success or failure
	 */
	callTrigger: async (
		triggerId: string,
		callback?: (markComplete: () => Promise<boolean>) => void
	): Promise<boolean> => {
		const {
			triggers,
			triggerLogs,
			userId,
			replaceActions,
			markTriggerLogComplete,
			queue,
			currentAction,
			isOnboardingActive,
		} = get();

		// Early return if there's already an action in the queue or a current action running
		if ((queue && queue.length > 0) || currentAction || isOnboardingActive) {
			return false;
		}

		// Ensure userId is available
		if (!userId) {
			console.error('User ID is required to call a trigger');
			return false;
		}

		// Check if the trigger exists in the store
		const trigger = triggers.find((t) => t.id === triggerId);
		if (!trigger) {
			console.warn(`Trigger with ID ${triggerId} not found`);
			return false;
		}

		// Check if trigger conditions are satisfied
		const isSatisfied = checkTrigger(trigger.trigger_condition, triggerLogs);

		if (isSatisfied) {
			// Handle callback type actions
			if (trigger.action && trigger.action.type === 'callback') {
				// Create a trigger log for callback actions
				const { log, error: logError } = await createTriggerLog(
					triggerId,
					userId,
					{
						completed: false, // Initially mark as not completed
						metadata: null,
					}
				);

				if (logError) {
					console.error(
						'Failed to create trigger log for callback action:',
						logError
					);
					return false;
				}

				const triggerLogId = log?.id;
				if (!triggerLogId) {
					console.error('Failed to get trigger log ID for callback');
					return false;
				}

				// Add the new log to the local state
				const newLog = log as TriggerLog;
				set((state) => ({
					triggerLogs: [...state.triggerLogs, newLog],
				}));

				// Create a function to mark this particular log as complete
				const markComplete = async (): Promise<boolean> => {
					return await markTriggerLogComplete(triggerLogId);
				};

				// Call the callback if provided
				if (callback) {
					callback(markComplete);
				}

				return true;
			}

			// Process regular actions with 'actions' array
			if (trigger.action && Array.isArray(trigger.action.actions)) {
				// Check if any of the actions are surveys
				const hasSurvey = trigger.action.actions.some(
					(action) => action.type === 'SURVEY'
				);

				// For surveys, create a log with completed=false that will be updated later
				// For non-surveys, create a log with completed=true immediately
				const { log, error: logError } = await createTriggerLog(
					triggerId,
					userId,
					{
						completed: !hasSurvey, // Only mark as completed if not a survey
						metadata: null,
					}
				);

				if (logError) {
					console.error('Failed to create trigger log:', logError);
					return false;
				}

				// Store the log ID for survey actions to update later
				const triggerLogId = log?.id;

				if (!triggerLogId) {
					console.error('Failed to get trigger log ID');
					return false;
				}

				// Add the new log to the local state
				const newLog = log as TriggerLog;
				set((state) => ({
					triggerLogs: [...state.triggerLogs, newLog],
				}));

				// Check for survey actions and add onSubmit callbacks
				const processedActions = trigger.action.actions.map(
					(action: ActionInput) => {
						// If it's a survey action, add an onSubmit callback
						if (action.type === 'SURVEY') {
							return {
								...action,
								id: action.id || uuidv4(),
								onSubmit: async (
									responses: Record<string, string | number | boolean>
								) => {
									// Save original onSubmit if it exists
									if (action.onSubmit) {
										action.onSubmit(responses);
									}

									// Update the trigger log to mark it as completed
									const { log: updatedLog, error } = await updateTriggerLog(
										triggerLogId,
										{
											completed: true,
										}
									);

									if (error) {
										console.error(
											'Failed to update trigger log as completed:',
											error
										);
									} else if (updatedLog) {
										// Update the local store with the updated log
										set((state) => ({
											triggerLogs: state.triggerLogs.map((log) =>
												log.id === triggerLogId ? updatedLog : log
											),
										}));
									}
								},
							} as SurveyAction;
						}

						return {
							...action,
							id: action.id || uuidv4(),
						};
					}
				);

				// Invoke actions
				replaceActions(processedActions);

				return true;
			}
		}

		return false;
	},
});
